import { computed } from '@ember/object';
import { isNone } from '@ember/utils';
import { attr } from '@ember-data/model';
import { getFormat } from '@square/dashboard-utilities/utils/i18n-deprecated/format';
import EmberDataBaseFragment from '@square/dashboard-data/models/ember-data-base-fragment';
import Money from '@square/dashboard-money/models/money';

/**
 * Fragment model representing a V2 seller_display_rate from the processing-fees API.
 *
 * V2 API Response Shape:
 * {
 *   "headline_rate_name": 1,  // HeadlineFeeRateName enum value
 *   "fee_rate": {
 *     "fee_rate_id": "RATE_ID",
 *     "variable_basis_points": 260,
 *     "fixed_cents": 10,
 *     "constraints": [{"type": "MIN_FEE", "amount_base_unit": 1}]
 *   }
 * }
 *
 * After serialization (camelCase):
 * {
 *   "headlineRateName": 1,  // HeadlineFeeRateName enum value (number)
 *   "feeRateId": "RATE_ID",
 *   "variableBasisPoints": 260,
 *   "fixedCents": 10,
 *   "constraints": [{"type": "MIN_FEE", "amountBaseUnit": 1}]
 * }
 *
 * Usage with HeadlineFeeRateName enum:
 *   import { HeadlineFeeRateName } from '@square/dashboard-protos/models/squareup/feeplans/pricing/headline_fee_rates';
 *   if (rate.headlineRateName === HeadlineFeeRateName.CARD_PRESENT) { ... }
 */
export default EmberDataBaseFragment.extend({
  // Raw V2 attributes (flattened from nested fee_rate object by serializer)
  // headlineRateName is a number matching HeadlineFeeRateName enum values
  headlineRateName: attr('number'),
  feeRateId: attr('string'),
  variableBasisPoints: attr('number'),
  fixedCents: attr('number'),
  constraints: attr('array', {
    defaultValue: () => []
  }),
  /**
   * Compares this rate with another for equality based on pricing values.
   *
   * Note: This intentionally only compares variableBasisPoints and fixedCents,
   * NOT feeRateId, headlineRateName, or constraints. This matches the existing
   * processing-fee.js fragment behavior where equality means "same price" rather
   * than "same rate object". Two rates with the same percentage and fixed fee
   * are considered equal even if they have different IDs or constraints, because
   * from a display/comparison perspective they represent the same cost to the merchant.
   *
   * @param {SellerDisplayRate} other - Another seller display rate to compare
   * @returns {boolean} True if rates have the same pricing values
   */
  equals(other) {
    if (isNone(other)) {
      return false;
    }
    return this.variableBasisPoints === other.get('variableBasisPoints') && this.fixedCents === other.get('fixedCents');
  },
  /**
   * Formatted rate string, e.g., "2.6% + 10¢"
   * Matches the interface of processing-fee.formatted
   */
  formatted: computed('variableBasisPoints', 'fixedCents', function () {
    return this.formatRate(this.variableBasisPoints, this.fixedCents);
  }),
  /**
   * Formatted constraints object, e.g., { MIN_FEE: "$1.00", MAX_FEE: "$10.00" }
   * Matches the interface of processing-fee.formattedConstraints
   * Note: Uses 'constraints.[]' since constraints is a plain array attr, not a fragmentArray
   */
  formattedConstraints: computed('constraints.[]', function () {
    if (!this.constraints) {
      return {};
    }
    return this.constraints.reduce((formattedConstraints, constraint) => {
      // Handle both snake_case (raw) and camelCase (normalized) property names
      const amount = constraint.amountBaseUnit ?? constraint.amount_base_unit;
      // eslint-disable-next-line no-param-reassign
      formattedConstraints[constraint.type] = this.formatCurrency(amount);
      return formattedConstraints;
    }, {});
  }),
  /**
   * Formats a currency amount in cents to a display string.
   * Uses getFormat() which is configured with the user's locale/currency.
   * @param {number} amount - Amount in cents (base units)
   * @returns {string} Formatted currency string, e.g., "$1" or "¥100"
   */
  formatCurrency(amount) {
    // Match the pattern from processing-fee.js for consistency
    const amountMoney = Money.fromCents(amount);
    return getFormat().currency(amountMoney.get('amount'), amountMoney.get('currencyCode'), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  },
  /**
   * Formats basis points and cents into a display string.
   * Uses getFormat() which is configured with the user's locale/currency.
   * @param {number} basisPoints - Variable rate in basis points (260 = 2.60%)
   * @param {number} cents - Fixed fee in cents
   * @returns {string|null} Formatted rate string, e.g., "2.6% + 10¢" or "2.6% + ¥10"
   */
  formatRate(basisPoints, cents) {
    // Handle explicit zeros (free rate) - both must be explicitly 0
    if (basisPoints === 0 && cents === 0) {
      return getFormat().percent(0);
    }
    // Return null if no meaningful rate values are present.
    // Uses falsy checks intentionally: 0 without a companion value is not displayable.
    // This matches V1 behavior in processing-fee.js:75-77
    if (!basisPoints && !cents) {
      return null;
    }
    let variable = '';
    if (basisPoints) {
      const options = {
        minimumFractionDigits: basisPoints < 100 ? 2 : 1,
        maximumFractionDigits: 2
      };
      variable = getFormat().percent(basisPoints / 100, options);
    }
    let fixed = '';
    if (cents) {
      // getFormat().currencyCent() uses the user's configured currency
      fixed = getFormat().currencyCent(cents);
    }
    const separator = fixed && variable ? ' + ' : '';
    return variable + separator + fixed;
  }
}).reopenClass({
  _modelNameForMigration: 'seller-display-rate'
});